import CustomModal from "@components/common/CustomModal";
import { updateValidationDocs } from "@components/Delivery/DeliveryDetails/Querys";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Container, Typography } from "@mui/material";
import { StoredMediaFileSchema } from "@repo/types";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import type { Doc } from "@types_def/models/delivery.types";
import React, { useEffect, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { Upload, UploadDropzone, UploadFormItem } from "@/components/common/upload";
import { getDeliveryDetailQueryKey } from "@/components/Delivery/cache";
import { useFindDeliveryByIdQuery } from "@/libs/generated/hooks";
import { parseDocs } from "@/utils/functions/Delivery";

const validationDocsSchema = z.object({
  id: z.number(),
  validationDocs: z.array(StoredMediaFileSchema.extend({ url: z.string().nullable() })),
});

type ValidationDocsFormData = z.infer<typeof validationDocsSchema>;

type Props = {
  data: { id: number; docs?: unknown; validationDocs?: unknown };
  revalidate?: () => void;
};

function UpdateValidationDocs({ data, revalidate }: Props) {
  const initialValidationDocs = useMemo(
    () => parseDocs(data.validationDocs),
    [data.validationDocs]
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const queryClient = useQueryClient();
  const { refetch: refetchDelivery } = useFindDeliveryByIdQuery(
    { GetByIdInput: { id: data.id } },
    { enabled: false } // We control when to fetch
  );

  const methods = useForm<ValidationDocsFormData>({
    reValidateMode: "onChange",
    resolver: zodResolver(validationDocsSchema),
    defaultValues: {
      id: data.id,
      validationDocs: initialValidationDocs,
    },
  });

  useEffect(() => {
    methods.reset({
      id: data.id,
      validationDocs: initialValidationDocs,
    });
  }, [data.id, initialValidationDocs, methods]);

  useWatch({ control: methods.control, name: "validationDocs" });
  const isFetching =
    useIsFetching({ queryKey: getDeliveryDetailQueryKey(data.id), exact: true }) > 0;

  // Add a local state for validationDocsData to force update after delete/add/edit
  const [validationDocsData, setValidationDocsData] = React.useState<Doc[]>(initialValidationDocs);

  // Update local state when data.validationDocs changes (initial load or refetch)
  useEffect(() => {
    // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
    setValidationDocsData(initialValidationDocs);
  }, [initialValidationDocs]);

  // Fetch latest docs after delete/add/edit without relying on query invalidation
  const fetchLatestDocs = async (): Promise<void> => {
    const { data: latest } = await refetchDelivery();
    if (
      latest !== null &&
      latest !== undefined &&
      latest.findDeliveryById !== null &&
      latest.findDeliveryById !== undefined &&
      latest.findDeliveryById.validationDocs !== null &&
      latest.findDeliveryById.validationDocs !== undefined
    ) {
      setValidationDocsData(parseDocs(latest.findDeliveryById.validationDocs));
    }
  };

  const handleUpdateValidationDocs = async (formData: ValidationDocsFormData) => {
    try {
      setIsSubmitting(true);
      const { id, validationDocs } = formData;
      // Update validation docs
      await updateValidationDocs({ id, validationDocs }, queryClient);
      if (revalidate) revalidate();
      methods.reset({ id: data.id, validationDocs });
      setIsSubmitting(false);
    } catch (_error) {
      setIsSubmitting(false);
    }
  };

  const handleDocsChange = async (newDocs: Doc[]) => {
    await handleUpdateValidationDocs({ id: data.id, validationDocs: newDocs });
    await fetchLatestDocs();
  };

  const handleDocsEdit = async (editedDoc: Doc, previousKey: string) => {
    const { data: latest } = await refetchDelivery();
    if (!latest?.findDeliveryById) throw new Error("Cannot load current validation documents");
    const docs = parseDocs(latest.findDeliveryById.validationDocs);
    if (!docs.some((doc) => doc.key === previousKey)) {
      throw new Error("The document changed. Refresh before replacing it.");
    }
    const updated = docs.map((doc) => (doc.key === previousKey ? { ...doc, ...editedDoc } : doc));
    methods.setValue("validationDocs", updated, { shouldDirty: true });
    await handleDocsChange(updated);
  };

  return (
    <Container maxWidth="md" sx={{ padding: 2 }}>
      <CustomModal
        button={
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting || isFetching}
          >
            Modifier Validation Documents
          </Button>
        }
        fullWidth
        title="Modifier les documents de validation"
        confirmText={isSubmitting || isFetching ? "Modification en cours..." : "Modifier"}
        confirmAction={methods.handleSubmit(handleUpdateValidationDocs)}
        disabled={isSubmitting || isFetching}
        color="primary"
        variant="contained"
        size="md"
        noAction={true}
      >
        <FormProvider {...methods}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Ajoutez ou modifiez les documents de validation pour cette livraison.
          </Typography>
          <Upload.Root>
            <UploadDropzone<ValidationDocsFormData, "validationDocs">
              meta={{
                folder: `delivery/${data.id.toString()}/docs/validationDocs/`,
                subFolder: String(data.id),
              }}
              name="validationDocs"
              cb={(docs) => {
                void handleDocsChange(docs);
              }}
            />
            <br />
            {validationDocsData.map((doc, index) => (
              <UploadFormItem<ValidationDocsFormData, `validationDocs.${number}`>
                name={`validationDocs.${index}`}
                key={doc.key}
                meta={{
                  folder: `delivery/${data.id.toString()}/docs/validationDocs/`,
                  subFolder: String(data.id),
                }}
                handleEdit={(editedDoc) => {
                  void handleDocsEdit(editedDoc, doc.key);
                }}
                handleDelete={(key) => {
                  const newDocs = validationDocsData.filter((item) => item.key !== key);
                  methods.setValue("validationDocs", newDocs, { shouldDirty: true });
                  void handleDocsChange(newDocs);
                }}
              />
            ))}
          </Upload.Root>
        </FormProvider>
      </CustomModal>
    </Container>
  );
}

export { UpdateValidationDocs };
